import { useState } from 'react';

import { useParams } from 'react-router-dom';

import { DocChronologyDoc } from '../../../../../../../../../../../backend/src/matter/subset/chronology/doc-chronology/doc-chronology-doc.entity';
import { useAddDocsToDocChronology } from '@/api/mutations/useAddDocsToDocChronology';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { useStore } from '@/store';

interface FileRelationship {
  id: string;
  name: string;
  doc?: { id: string; fileId?: string };
}

interface DocChronRowRelationshipsProps {
  chronDoc: DocChronologyDoc;
}

export function DocChronRowRelationships({ chronDoc }: DocChronRowRelationshipsProps) {
  const { matterId, subsetId, docChronologyId } = useParams();

  // Extract parent/child file relationships (added by backend)
  const file = chronDoc.doc.file as typeof chronDoc.doc.file & {
    parentFiles?: FileRelationship[];
    childFiles?: FileRelationship[];
  };
  const parentFiles = file.parentFiles ?? [];
  const childFiles = file.childFiles ?? [];

  const [isAttachmentsPopoverOpen, setIsAttachmentsPopoverOpen] = useState(false);

  const { mutate: addDocs, isLoading: isAddingDocs } = useAddDocsToDocChronology();
  const { openReferenceDrawer } = useStore();

  if (parentFiles.length === 0 && childFiles.length === 0) {
    return null;
  }

  return (
    <>
      {/* Parent relationships */}
      {parentFiles.length > 0 && (
        <>
          <span className="text-gray-400">
            attachment of{' '}
            {(() => {
              const formatter = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });
              const parts = formatter.formatToParts(parentFiles.map((p) => p.name));

              return parts.map((part, index) => {
                if (part.type === 'element') {
                  const parent = parentFiles.find((p) => p.name === part.value);
                  return (
                    <span key={`${parent?.id}-${index}`} className="inline-flex items-center gap-1">
                      <button
                        className="text-blue-500 hover:text-blue-600"
                        onClick={() => parent?.doc?.id && openReferenceDrawer({ docId: parent.doc.id })}
                      >
                        {part.value}
                      </button>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="sm"
                            disabled={isAddingDocs}
                            className="px-1 text-[11px]"
                            onClick={() => {
                              if (!matterId || !subsetId || !docChronologyId || !parent?.doc?.id) return;
                              addDocs({
                                matterId,
                                subsetId,
                                docChronologyId,
                                docIds: [parent.doc.id],
                              });
                            }}
                          >
                            <Icon className="lucide--git-branch-plus" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" sideOffset={6}>
                          Add to this chronology
                        </TooltipContent>
                      </Tooltip>
                    </span>
                  );
                }
                return (
                  <span key={index} className="pl-0.5">
                    {part.value}
                  </span>
                );
              });
            })()}
          </span>
        </>
      )}

      {/* Child relationships */}
      {childFiles.length > 0 && (
        <Popover open={isAttachmentsPopoverOpen} onOpenChange={setIsAttachmentsPopoverOpen}>
          <PopoverTrigger className="flex items-center gap-1 text-green-600 duration-150 hover:text-green-700">
            <Icon className="lucide--paperclip" />
            {childFiles.length} attachment{childFiles.length !== 1 ? 's' : ''}
          </PopoverTrigger>
          <PopoverContent
            align="start"
            side="left"
            className="-mt-2 px-3 py-2 text-xs"
            sideOffset={8}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="mb-1 flex gap-2">
              <div className="flex items-center gap-1.5 font-medium">
                <Icon className="lucide--paperclip text-green-600" />
                Attachments
              </div>
            </div>

            <ol className="mb-1 max-h-48 space-y-1.25 overflow-y-auto pt-0.75 [counter-reset:item]">
              {childFiles.map((child) => (
                <li
                  key={child.id}
                  className="flex items-center gap-1 [counter-increment:item] before:font-mono before:text-gray-400 before:content-[counter(item)'.']"
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        disabled={isAddingDocs}
                        className="mr-0.75 px-1 text-[11px]"
                        onClick={() => {
                          if (!matterId || !subsetId || !docChronologyId || !child.doc?.id) return;
                          addDocs({
                            matterId,
                            subsetId,
                            docChronologyId,
                            docIds: [child.doc.id],
                          });
                        }}
                      >
                        <Icon className="lucide--git-branch-plus" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right" sideOffset={6}>
                      Add to this chronology
                    </TooltipContent>
                  </Tooltip>

                  <button
                    className="w-full truncate text-left text-blue-500 hover:text-blue-600"
                    onClick={() => {
                      if (child.doc?.id) {
                        openReferenceDrawer({ docId: child.doc.id });
                        setIsAttachmentsPopoverOpen(false);
                      }
                    }}
                  >
                    {child.name}
                  </button>
                </li>
              ))}
            </ol>
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}
